import SaveAsIcon from '@mui/icons-material/SaveAs';
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "../../../../constants/appRoutes";
import { CardSpacing } from "../../../../constants/cardSpacing";
import { FormHelper } from "../../../../helpers/formHelper";
import { getCultureDisplayName } from "../../../../helpers/localeHelper";
import { buildRegionizeRoute } from "../../../../helpers/regionRouteHelper";
import ActionBarDetail from "../../../../shared/components/common/actionBarDetail";
import { LabelBadge } from "../../../../shared/components/common/labelBadge";
import SectionWithHeading from "../../../../shared/components/common/sectionWithHeading";
import { Spacing } from "../../../../shared/components/common/spacing";
import { Toaster } from "../../../../shared/components/common/toaster";
import ButtonWrapper from "../../../../shared/formUtilities/useButton";
import InputWrapper from "../../../../shared/formUtilities/useInput";
import { CkRichEditor } from "../../../../shared/formUtilities/useRichEditor";
import TextWrapper from "../../../../shared/formUtilities/useTextWrapper";
import { ComparisonSchema } from "../../../../shared/validations/schema";
import { ComparisonService } from "../comparisonService";
import { RegionTypes } from '../../../../constants/regionType';

export const ComparisonCreate = () => {
  const comparisonService = new ComparisonService();
  const [comparisonData, setComparisonData] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const id = useParams()?.id;

  const initialValues = {
    slug: '',
    languages: FormHelper.createLanguageFormArray({
      withTitle: true,
      withDescription: true,
      isRequired: true,
      withMetaDescription: true,
      withIsInvestors: true,
      withBorrowers: true,
      withOverview: true,
      withSubTitle: true,
      withFeatureComparison: true
    }),
    published: false,
    hreflangGlobal: false,
    hreflangSA: false,
  };

  const form = useFormik({
    initialValues: initialValues,
    validationSchema: ComparisonSchema,
    onSubmit: () => {
      if (!form.values.hreflangSA && !form.values.hreflangGlobal) {
        Toaster({
          msg: 'You should add at least one alternate language tag',
        });
      } else {
        onSaveOrUpdate();
      }
    },
    ...FormHelper.formikProps(),
  });

  useEffect(() => {
    if (id) {
      setLoading(true);
      getComparisonDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getComparisonDetails = () => {
    comparisonService.getComparisonDetail(id).then(resp => {
      setLoading(false);
      setComparisonData(resp.data);
      let comparison = resp.data;
      let languages = resp.data?.languages;
      form.setValues(resp.data);
      if (comparison?.hrefLangKeys && comparison?.hrefLangKeys?.length) {
        form.setFieldValue(
          'hreflangSA',
          !!comparison?.hrefLangKeys?.find(
            (lang) => lang === RegionTypes.SaudiArabia
          )
        );
        form.setFieldValue(
          'hreflangGlobal',
          !!comparison?.hrefLangKeys?.find((lang) => lang === RegionTypes.Global)
        );
      }
      if (!languages?.length) {
        form.setFieldValue(
          'languages',
          FormHelper.createLanguageFormArray({
            withTitle: true,
            withDescription: true,
            isRequired: true,
            withMetaDescription: true,
            withIsInvestors: true,
            withBorrowers: true,
            withOverview: true,
            withSubTitle: true,
            withFeatureComparison: true
          })
        );
      }

    }).catch(err => {
      setLoading(false);
    });
  };

  const onUpdate = (formData) => {
    comparisonService
      .updateComparison(id, formData)
      .then((resp) => {
        setLoading(false);
        onNavigate();
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const onSave = (formData) => {
    comparisonService
      .addComparison(formData)
      .then((resp) => {
        setLoading(false);
        onNavigate();
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const onNavigate = () => {
    navigate(buildRegionizeRoute(`${AppRoutes.CompetitorsComparisons}`));
  };

  const onSaveOrUpdate = () => {
    if (form.isValid) {
      setLoading(true);
      let model = form.values;
      let hrefLangKeys = [];
      if (model?.hreflangGlobal) {
        hrefLangKeys.push(RegionTypes.Global);
      }
      if (model?.hreflangSA) {
        hrefLangKeys.push(RegionTypes.SaudiArabia);
      }
      delete model.hreflangGlobal;
      delete model.hreflangSA;
      model['hrefLangKeys'] = hrefLangKeys;
      if (id) {
        onUpdate(model);
      } else {
        onSave(model);
      }
    } else {
      Toaster({ msg: 'Please fill the required fields' });
    }
  };

  const getTitle = () => {
    if (comparisonData && comparisonData?.languages && comparisonData?.languages?.length) {
      return comparisonData?.languages[0]?.title;
    } else {
      return 'Add Review';
    }
  };

  const cultureName = (language) => {
    return getCultureDisplayName(language?.culture);
  };

  const isArabic = (language) => {
    return getCultureDisplayName(language?.culture) === 'Arabic';
  };

  return <Box>
    <FormikProvider value={form}>
      <ActionBarDetail
        loading={loading}
        userName={`${getTitle()}`}></ActionBarDetail>
      <Spacing></Spacing>
      <SectionWithHeading
        statusLabel={
          <LabelBadge
            classList={
              !form.values.published
                ? '!bg-red-200 !text-red-500 !px-4 !py-1'
                : '!px-4 !py-1'
            }
            label={form.values.published ? 'Published' : 'Draft'}></LabelBadge>
        }
        loading={loading}
        heading={'Review Template'}
        actionButtons={
          <Box>
            <ButtonWrapper
              icon={<SaveAsIcon></SaveAsIcon>}
              classList="!bg-red-500"
              label="Save as draft"
              onClick={(e) => {
                form.setFieldValue('published', false);
                form.submitForm();
              }}></ButtonWrapper>
          </Box>
        }
        isEditButton={true}
        isEditToggle={true}
        onEditInfo={(e) => {
          form.setFieldValue('published', true);
          form.submitForm();
        }}
        children={
          <Box className="w-full">
            <fieldset style={{ height: 'fit-content' }}>
              <SectionWithHeading heading={'Basic Info'}>
                <Box className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputWrapper
                    name={'slug'}
                    key={'slug'}
                    label={'Slug Name'}></InputWrapper>
                </Box>
              </SectionWithHeading>
              <Spacing space={CardSpacing.lg}></Spacing>
              <SectionWithHeading heading={'Alternate Language Tags'}>
                <Box className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormControlLabel
                    className="!items-start"
                    control={
                      <Checkbox
                        onChange={(e) => {
                          form.setFieldValue(
                            'hreflangGlobal',
                            e.target.checked
                          );
                        }}
                        name="hreflangGlobal"
                        checked={form.values.hreflangGlobal}
                        value={form.values.hreflangGlobal}
                      />
                    }
                    label={
                      <Box className="flex flex-col space-y-1">
                        <Box>
                          Enable alternate language tags in Global platform (AE)
                        </Box>
                        <Box className="flex flex-col space-y-1">
                          <small className="!caption">
                            1) hreflang tags values will be 'en', 'ar' and
                            'x-default'
                          </small>
                          <small className="!caption">
                            2) href (url) will be dynamic
                          </small>
                        </Box>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    className="!items-start"
                    control={
                      <Checkbox
                        onChange={(e) => {
                          form.setFieldValue('hreflangSA', e.target.checked);
                        }}
                        name="hreflangSA"
                        checked={form.values.hreflangSA}
                        value={form.values.hreflangSA}
                      />
                    }
                    label={
                      <Box className="flex flex-col space-y-1">
                        <Box>
                          Enable alternate language tags in Saudi Arabia
                          platform (SA)
                        </Box>
                        <Box className="flex flex-col space-y-1">
                          <small className="!caption">
                            1) hreflang tags values will be 'en-sa', 'ar-sa' and
                            'x-default'
                          </small>
                          <small className="!caption">
                            2) href (url) will be dynamic
                          </small>
                        </Box>
                      </Box>
                    }
                  />
                </Box>
              </SectionWithHeading>
            </fieldset>
            <Spacing space={CardSpacing.lg}></Spacing>
            <fieldset className="flex flex-col space-y-4">
              <FieldArray name="languages">
                <Box
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  style={{ height: 'fit-content' }}>
                  {form.values?.languages?.length > 0 &&
                    form?.values?.languages.map((language, index) => {
                      return (
                        <Box
                          className="flex flex-col space-y-4"
                          key={index}>

                          <>
                            <SectionWithHeading
                              heading={`Meta Tags - ${cultureName(language)}`}
                              children={
                                <Box className="flex flex-0 flex-col justify-start space-y-3">
                                  <Box className="relative">
                                    <InputWrapper
                                      name={`languages[${index}].metaTitle`}
                                      key={`languages[${index}].metaTitle`}
                                      label={'Meta Title'}></InputWrapper>
                                    <TextWrapper
                                      classList="!text-end "
                                      type="caption"
                                      text={
                                        <span>
                                          <span
                                            className={`${form.values.languages[index]
                                              .metaTitle?.length > 75
                                              ? '!text-red-500'
                                              : 'text-primary'
                                              }`}>{`${form.values.languages[index]
                                                .metaTitle?.length || 0
                                                }`}</span>
                                          /75
                                        </span>
                                      }></TextWrapper>
                                  </Box>
                                  <Box className="relative">
                                    <InputWrapper
                                      isTextArea
                                      rows={3}
                                      name={`languages[${index}].metaDescription`}
                                      key={`languages[${index}].metaDescription`}
                                      label={'Meta Description'}></InputWrapper>
                                    <TextWrapper
                                      classList="!text-end "
                                      type="caption"
                                      text={
                                        <span>
                                          <span
                                            className={`${form.values.languages[index]
                                              .metaDescription?.length > 170
                                              ? '!text-red-500'
                                              : 'text-primary'
                                              }`}>{`${form.values.languages[index]
                                                .metaDescription?.length || 0
                                                }`}</span>
                                          /170
                                        </span>
                                      }></TextWrapper>
                                  </Box>
                                </Box>
                              }></SectionWithHeading>
                          </>
                          <>
                            <SectionWithHeading
                              heading={`Review Details - ${cultureName(language)}`}
                              children={
                                <Box className="flex flex-0 flex-col justify-start space-y-3">
                                  <Box className="relative">
                                    <InputWrapper
                                      name={`languages[${index}].title`}
                                      key={`languages[${index}].title`}
                                      label={'Title'}></InputWrapper>
                                  </Box>
                                  <Box className="relative">
                                    <InputWrapper
                                      name={`languages[${index}].subTitle`}
                                      key={`languages[${index}].subTitle`}
                                      label={'Subtitle'}></InputWrapper>
                                  </Box>
                                  <Box className="relative">
                                    <InputWrapper
                                      isTextArea
                                      rows={4}
                                      name={`languages[${index}].featureComparison`}
                                      key={`languages[${index}].featureComparison`}
                                      label={'Feature Comparison Description'}></InputWrapper>
                                  </Box>
                                </Box>
                              }></SectionWithHeading>
                          </>
                          <SectionWithHeading
                            heading={`Overview - ${cultureName(language)}`}
                            children={
                              <Box className="flex flex-0 flex-col justify-start space-y-6 h-[400px]">
                                <CkRichEditor
                                  isArabic={isArabic(language)}
                                  key={index}
                                  value={language?.overview}
                                  onChange={(data) => {
                                    form.setFieldValue(
                                      `languages[${index}].overview`,
                                      data
                                    );
                                  }}></CkRichEditor>
                              </Box>
                            }></SectionWithHeading>
                          <SectionWithHeading
                            heading={`For Investors - ${cultureName(language)}`}
                            children={
                              <Box className="flex flex-0 flex-col justify-start space-y-6">
                                <CkRichEditor
                                  isArabic={isArabic(language)}
                                  key={index}
                                  value={language?.forInvestors}
                                  onChange={(data) => {
                                    form.setFieldValue(
                                      `languages[${index}].forInvestors`,
                                      data
                                    );
                                  }}></CkRichEditor>
                              </Box>
                            }></SectionWithHeading>
                          <>
                            <SectionWithHeading
                              heading={`For Borrowers - ${cultureName(language)}`}
                              children={
                                <Box className="flex flex-0 flex-col justify-start space-y-6">
                                  <CkRichEditor
                                    isArabic={isArabic(language)}
                                    key={index}
                                    value={language?.forBorrowers}
                                    onChange={(data) => {
                                      form.setFieldValue(
                                        `languages[${index}].forBorrowers`,
                                        data
                                      );
                                    }}></CkRichEditor>
                                </Box>
                              }></SectionWithHeading>
                          </>
                        </Box>
                      );
                    })}
                </Box>
              </FieldArray>
            </fieldset>
          </Box>
        }></SectionWithHeading>
    </FormikProvider>
  </Box>;
};